import type { RecoilState } from "recoil"

import {
  allAreasState,
  allEdgesState,
  allNodesState,
} from "~/containers/LegacyDesigner/state/elements"
import {
  allPanelsState,
  selectedModuleRemoteState,
  selectedModuleState,
} from "~/containers/LegacyDesigner/state/elements/panels"
import { areasOfPanelsIndexState } from "~/containers/LegacyDesigner/state/elements/relations"
import { Index1ToMany } from "~/utils/join"

import type { Design } from "./design-model"
import { designLocalState } from "./design-state"
import {
  getSolarModuleEquipmentFromDesign,
  mapSolarModuleToSelectedModule,
} from "./solar-module"

/**
 * A Recoil setter. Both `MutableSnapshot.set` (RecoilRoot `initializeState`)
 * and the `set` handed to `useRecoilCallback` satisfy it.
 */
export type RecoilSet = <T>(recoilState: RecoilState<T>, newValue: T) => void

/**
 * Loads a Design into the local design and the Legacy Designer elements
 * (nodes, edges, install areas, panels, area-to-panel index, selected module).
 * Leaves the remote design to the caller.
 */
export const loadDesignIntoElements = (set: RecoilSet, design: Design): void => {
  set(designLocalState, design)
  set(allNodesState, design.nodes)
  set(allEdgesState, design.edges)
  set(allAreasState, design.installAreas)
  set(allPanelsState, design.panels)
  set(areasOfPanelsIndexState, new Index1ToMany(design.installAreasToPanels))
  const solarModule = getSolarModuleEquipmentFromDesign(design)
  if (solarModule) {
    const selectedModule = mapSolarModuleToSelectedModule(solarModule)
    set(selectedModuleState, selectedModule)
    set(selectedModuleRemoteState, selectedModule)
  }
}
